/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, May 13th 2024, 12:24:46 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { replaceKeyPreservePostion } from "lib/utils";
import { OSActionButton, OSDeleteButton } from "ui-widgets";
import { EmptyJSONConfiguration } from "./EmptyJSONConfiguration";

type TParams = { [key: string]: any };

type JSONConfigurationParamsProps = {
  onChangeCustomConfiguration: (params: TParams) => void;
  params: TParams;
};

export const JSONConfigurationParams = ({ params, onChangeCustomConfiguration }: JSONConfigurationParamsProps) => {
  const onAddNewParam = () => {
    const newKey = `Key-${Object.keys(params).length}`;
    const newParams = { ...params, [newKey]: "" };
    onChangeCustomConfiguration(newParams);
  };

  const onRemoveCustomConfiguration = (key: string) => {
    const newParams = { ...params };
    delete newParams[key];
    onChangeCustomConfiguration(newParams);
  };

  const onChangeParamKey = (oldKey: string, newKey: string) => {
    const newParams = replaceKeyPreservePostion(params, oldKey, newKey);
    console.log("New Params", newParams);
    onChangeCustomConfiguration(newParams);
  };

  const onChangeParamValue = (key: string, value: string) => {
    const newParams = { ...params, [key]: value };
    onChangeCustomConfiguration(newParams);
  };

  return (
    <Stack spacing={2}>
      {Object.keys(params).map((paramKey, index) => (
        <Stack direction="row" spacing={2} key={`${index}`}>
          <TextField
            label="Key"
            placeholder="Key"
            value={paramKey}
            onChange={(e) => onChangeParamKey(paramKey, e.target.value)}
          />
          <TextField
            label="Value"
            placeholder="Value"
            value={params[paramKey]}
            onChange={(e) => onChangeParamValue(paramKey, e.target.value)}
          />
          <Box>
            <OSDeleteButton onClick={() => onRemoveCustomConfiguration(paramKey)} />
          </Box>
        </Stack>
      ))}
      {Object.keys(params).length === 0 && <EmptyJSONConfiguration />}
      <Tooltip title={<Typography>Add custom configuration</Typography>} placement="top">
        <Box sx={{ ml: "auto" }}>
          <OSActionButton actionStyle="add" buttonType="floating" onClick={onAddNewParam} />
        </Box>
      </Tooltip>
    </Stack>
  );
};
